import Input from "Components/Form/Input";
import { Field, Form, useFormikContext } from "formik";
import { useState, useEffect, ChangeEvent } from "react";
import { usePreOrderDetails } from "Pages/PreOrderConvertionIAUnit/hooks/usePreOrderDetails";
import { useParams } from "react-router";
import { Edit, Check } from "lucide-react";
import { Box } from "styles/globalComponents";
import { Button } from "Components/Button";
import { InputPhone } from "Components/Form/PhoneInput";
import * as yup from "yup";
import { isValidPhoneMask } from "Utils/Phone";
import { MaskCPF } from "Utils/Masks";
import { isCPF } from "Services/schema/validations";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { getUnitsByUser, Unit } from "Requests/PreOrder";
import { useQuery } from "react-query";
import Select from "Components/Form/Select";
import { InfoDetailsStyled } from "./styles";

const GENDER_OPTIONS = [
  { id: "m", name: "Masculino" },
  { id: "f", name: "Feminino" },
];

// Função de validação de nome reutilizável
const nameValidation = yup
  .string()
  .matches(
    /^[\p{L}\s\-'.,]+$/u,
    "Nome deve conter apenas letras, espaços, hífens, aspas e pontos"
  );

export type PersonInfo = {
  full_name: string;
  email: string;
  phone: string;
  doctor: string;
  crm: string;
  document: string;
  mothers_full_name: string;
  birth_date: string;
  gender: string;
  insurance_operator: string | null;
  health_insurance_card_number: string | null;
  have_health_insurance: string | null;
  unit_id: string;
};

const validatePhone = (phone: string) => {
  const [ddi, ddd, n1, n2] = formatPhoneNumberIntl(phone).split(" ");

  if (!ddi || !ddd || !n1 || !n2) return false;

  return true;
};

export const personData = yup.object({
  name: nameValidation.required("Nome do paciente é obrigatório"),
  document: yup
    .string()
    .test("document", "CPF inválido", isCPF)
    .required("Documento é obrigatório"),
  email: yup.string().email("Email inválido").required("Email é obrigatório"),
  birth_date: yup
    .string()
    .required("Data de nascimento é obrigatória")
    .test("birth_date", "Data inválida", (value) => {
      if (!value?.trim()) return false;
      return !Number.isNaN(Date.parse(value));
    }),
  gender: yup
    .string()
    .oneOf(["m", "f"], "Selecione o gênero biológico")
    .required("Gênero biológico é obrigatório"),
  doctor: nameValidation
    .nullable()
    .test("doctor-validation", "Nome do médico deve ser válido", (value) => {
      if (!value || value.trim() === "") return true; // Campo vazio é válido
      return nameValidation.isValidSync(value);
    }),
  crm: yup.string().nullable(),
  mothers_full_name: nameValidation
    .nullable()
    .test("mother-validation", "Nome da mãe deve ser válido", (value) => {
      if (!value || value.trim() === "") return true; // Campo vazio é válido
      return nameValidation.isValidSync(value);
    }),
  phone: yup
    .string()
    .required("Telefone é obrigatório")
    .test("phone", "Telefone inválido", (value) => {
      if (!value) return false;

      try {
        // Tentar validar com a função padrão primeiro

        const validPhone = validatePhone(value);

        if (!validPhone) return false;

        const isValid = isValidPhoneMask(value);
        if (isValid) return true;

        // Se falhar, tentar formatar e validar novamente
        // Para telefones brasileiros: +55 11 99999-9999
        let cleanValue = value
          .replace(/[^\d+\s+]/g, "") // Manter apenas dígitos, espaços e +
          .replace(/\s+/g, " ") // Normalizar espaços
          .trim();

        // Garantir que tenha o formato +55 11 999999999
        if (cleanValue.startsWith("+")) {
          // Remover hífens e formatar corretamente
          cleanValue = cleanValue.replace(/-/g, "");

          // Validar novamente
          return isValidPhoneMask(cleanValue);
        }

        // Se não tem o +, tentar adicionar e validar
        if (cleanValue.length >= 10) {
          const withPlus = `+${cleanValue}`;
          return isValidPhoneMask(withPlus);
        }

        return false;
      } catch (error) {
        console.warn("Erro na validação do telefone:", error);
        return false;
      }
    }),
  insurance_operator: yup.string().optional(),
  health_insurance_card_number: yup.string().optional(),
  have_health_insurance: yup.string().optional(),
  unit_id: yup.string().required("Unidade é obrigatória"),
});

export type Values = yup.InferType<typeof personData>;

interface InfoDetailsProps {
  blockUserInteraction: boolean;
  forceEditMode: boolean;
  onEditModeChange: (editMode: boolean) => void;
  registeredHealthFilesCount?: number;
  iaData?: {
    requestingDoctor?: string;
    doctorCRM?: string;
  };
}

export const InfoDetails = ({
  blockUserInteraction,
  forceEditMode,
  onEditModeChange,
  registeredHealthFilesCount = 0,
  iaData,
}: InfoDetailsProps) => {
  const [editFields, setEditFields] = useState(false);

  const { id } = useParams<{ id: string }>();

  const preOderDetailsAPI = usePreOrderDetails(Number(id));

  const formik = useFormikContext<Values>();

  const units = useQuery<Unit[]>({
    queryFn: getUnitsByUser,
    queryKey: ["user-units"],
    refetchOnWindowFocus: false,
  });

  let patientPhone = "";

  const {
    ddi,
    ddd,
    phone,
    doctor,
    crm,
    email,
    gender,
    unit_id,
    birth_date,
    health_files: healthFilesFromApi,
  } = preOderDetailsAPI.data || {};

  const hasRegisteredHealthFiles =
    registeredHealthFilesCount > 0 ||
    (healthFilesFromApi && healthFilesFromApi.length > 0);

  let customDdi = "55";

  if (ddi) {
    customDdi = ddi.replace(/\D/g, "");
  }

  if (phone) {
    if (phone.startsWith("+")) {
      patientPhone = phone;
    } else {
      const ddiClean = customDdi;
      const dddClean = ddd?.replace(/[^\d]/g, "");
      const phoneClean = phone?.replace(/[^\d]/g, "");
      const slickedPhone = `${phoneClean?.slice(0, -4)}-${phoneClean?.slice(
        -4
      )}`;

      if (ddiClean && dddClean && phoneClean) {
        patientPhone = `+${ddiClean} ${dddClean} ${slickedPhone}`;
      }
    }
  }

  const getGenders = (patientGender?: string) => {
    switch (patientGender?.toLowerCase()) {
      case "m":
        return "Masculino";
      case "f":
        return "Feminino";
      default:
        return "Indefinido";
    }
  };

  useEffect(() => {
    if (unit_id == null) {
      formik.setFieldValue("unit_id", "");
      return;
    }

    formik.setFieldValue("unit_id", String(unit_id));
  }, [unit_id]);

  useEffect(() => {
    formik.setFieldValue("phone", patientPhone);
  }, [patientPhone]);

  useEffect(() => {
    if (formik && patientPhone && !formik.values.phone) {
      const formattedPhone = patientPhone.trim();

      if (formattedPhone) {
        formik.setFieldValue("phone", formattedPhone);

        setTimeout(() => {
          formik.setFieldTouched("phone", true);

          setTimeout(() => {
            formik.validateField("phone");
          }, 50);
        }, 100);
      }
    }
  }, [patientPhone, formik?.values.phone]);

  useEffect(() => {
    if (forceEditMode) {
      setEditFields(true);
    }
  }, [forceEditMode, formik]);

  useEffect(() => {
    if (!editFields && forceEditMode) {
      onEditModeChange(false);
    }
  }, [editFields, forceEditMode, onEditModeChange]);

  useEffect(() => {
    if (iaData && formik) {
      const updates: Partial<Values> = {};

      if (iaData.requestingDoctor && !formik.values.doctor) {
        updates.doctor = iaData.requestingDoctor;
      }

      if (iaData.doctorCRM && !formik.values.crm) {
        updates.crm = iaData.doctorCRM;
      }

      if (Object.keys(updates).length > 0) {
        formik.setValues((prev) => ({
          ...prev,
          ...updates,
        }));
      }
    }
  }, [iaData, formik]);

  const isUploadDisabled = !editFields || blockUserInteraction;

  const selectedUnitLabel =
    units.data?.find((unit) => String(unit.id) === formik.values.unit_id)
      ?.name ?? "Não informado";

  const handleSelectedUnitChange = (event: ChangeEvent<HTMLSelectElement>) => {
    formik.setFieldValue("unit_id", event.target.value);
  };

  return (
    <>
      <Form id="teste">
        <InfoDetailsStyled className="tab-item patient-info">
          <div
            className="patient"
            style={{ display: "flex", flexDirection: "column", gap: "5px" }}
          >
            <Box display="flex" justify="space-between" align="center">
              <h3
                style={{
                  color: "var(--color-purple)",
                  fontSize: "1.2rem",
                  marginBottom: "0",
                }}
              >
                Dados do paciente
              </h3>

              {editFields ? (
                <Button
                  classButton="edit-btn"
                  onClick={() => setEditFields(false)}
                  disabled={formik.isSubmitting || blockUserInteraction}
                >
                  <Check size={15} />
                </Button>
              ) : (
                <Button
                  classButton="edit-btn"
                  onClick={() => setEditFields(!editFields)}
                  disabled={blockUserInteraction}
                >
                  <Edit size={15} />
                </Button>
              )}
            </Box>

            <div
              className="item"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <span>
                  <strong>Nome:</strong>{" "}
                </span>

                {editFields ? (
                  <Field
                    id="name"
                    type="text"
                    onChange={formik.handleChange}
                    value={formik.values.name}
                    component={Input}
                    className="input _opacity-gray"
                    error={formik.touched.name && formik.errors.name}
                  />
                ) : (
                  <span style={{ fontSize: "0.9rem" }}>
                    {formik.values.name}
                  </span>
                )}
              </div>
            </div>

            <div
              className="item"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <span>
                  <strong>CPF:</strong>{" "}
                </span>

                {editFields ? (
                  <Field
                    id="document"
                    type="text"
                    value={formik.values.document}
                    onChange={formik.handleChange}
                    component={Input}
                    className="input _opacity-gray"
                    error={formik.touched.document && formik.errors.document}
                  />
                ) : (
                  <span style={{ fontSize: "0.9rem" }}>
                    {MaskCPF(formik.values.document)}
                  </span>
                )}
              </div>
            </div>

            <div
              className="item"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <span>
                  <strong style={{ fontSize: "0.9rem" }}>Telefone: </strong>
                </span>

                {editFields ? (
                  <Field
                    id="phone"
                    type="text"
                    value={formik.values.phone}
                    onChange={(value: string) =>
                      formik.setFieldValue("phone", value)
                    }
                    onBlur={() => {
                      // Garantir que o campo seja marcado como touched
                      if (!formik.touched.phone) {
                        formik.setFieldTouched("phone", true);
                      }
                    }}
                    component={InputPhone}
                    className="input _opacity-gray"
                    error={formik.touched.phone && formik.errors.phone}
                  />
                ) : (
                  <span style={{ fontSize: "0.9rem" }}>
                    {formik.values.phone}
                  </span>
                )}
              </div>
            </div>

            <div
              className="item"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <span>
                  <strong>Data de nascimento: </strong>
                </span>

                {editFields ? (
                  <Field
                    id="birth_date"
                    type="date"
                    value={formik.values.birth_date || ""}
                    onChange={formik.handleChange}
                    onBlur={() => {
                      if (!formik.touched.birth_date) {
                        formik.setFieldTouched("birth_date", true);
                      }
                    }}
                    component={Input}
                    className="input _opacity-gray"
                    error={
                      formik.touched.birth_date && formik.errors.birth_date
                    }
                  />
                ) : (
                  <span style={{ fontSize: "0.9rem" }}>
                    {birth_date ? birth_date.split(" ")[0] : "Não informado"}
                  </span>
                )}
              </div>
            </div>

            <div
              className="item"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <span>
                  <strong>Email: </strong>
                </span>

                {editFields ? (
                  <Field
                    id="email"
                    type="email"
                    onChange={formik.handleChange}
                    value={formik.values.email || ""}
                    component={Input}
                    className="input _opacity-gray"
                    error={formik.touched.email && formik.errors.email}
                  />
                ) : (
                  <span style={{ fontSize: "0.9rem" }}>
                    {formik.values.email || email || "Não informado"}
                  </span>
                )}
              </div>
            </div>

            <div
              className="item"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <span>
                  <strong>Gênero biológico: </strong>
                </span>
                {editFields ? (
                  <Select
                    id="gender"
                    placeholder="Selecione o gênero"
                    optionName="name"
                    options={GENDER_OPTIONS}
                    readOnly={false}
                    value={formik.values.gender}
                    onChange={({ target: { value } }: any) =>
                      formik.setFieldValue("gender", value)
                    }
                  />
                ) : (
                  <span style={{ fontSize: "0.9rem" }}>
                    {getGenders(formik.values.gender || gender)}
                  </span>
                )}
              </div>
              {editFields && formik.touched.gender && formik.errors.gender ? (
                <span className="error-message">{formik.errors.gender}</span>
              ) : null}
            </div>
          </div>

          {/* Seção Dados do Convênio desativada no carrinho
          <div
            className="insurance"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            <h3
              style={{
                color: "var(--color-purple)",
                fontSize: "1.2rem",
                marginBottom: "0",
              }}
            >
              Dados do Convênio
            </h3>

            <div className="item" style={{ display: "flex", gap: "5px" }}>
              <span>
                <strong>Operadora: </strong>
              </span>
              {editFields ? (
                <Field
                  id="insurance_operator"
                  type="text"
                  value={formik.values.insurance_operator}
                  onChange={formik.handleChange}
                  component={Input}
                  className="input _opacity-gray"
                  error={
                    formik.touched.insurance_operator &&
                    formik.errors.insurance_operator
                  }
                />
              ) : (
                <span style={{ fontSize: "0.9rem" }}>
                  {formik.values.insurance_operator || "Não informado"}
                </span>
              )}
            </div>

            <div className="item" style={{ display: "flex", gap: "5px" }}>
              <span>
                <strong>N° da carteirinha: </strong>
              </span>
              {editFields ? (
                <Field
                  id="health_insurance_card_number"
                  type="text"
                  value={
                    formik.values.health_insurance_card_number ||
                    health_insurance_card_number ||
                    ""
                  }
                  onChange={formik.handleChange}
                  component={Input}
                  className="input _opacity-gray"
                  error={
                    formik.touched.health_insurance_card_number &&
                    formik.errors.health_insurance_card_number
                  }
                />
              ) : (
                <span style={{ fontSize: "0.9rem" }}>
                  {formik.values.health_insurance_card_number ||
                    "Não informado"}
                </span>
              )}
            </div>

            <div className="item" style={{ display: "flex", gap: "5px" }}>
              <span>
                <strong>Plano: </strong>
              </span>
              {editFields ? (
                <Field
                  id="have_health_insurance"
                  type="text"
                  value={
                    formik.values.have_health_insurance ||
                    have_health_insurance ||
                    ""
                  }
                  onChange={formik.handleChange}
                  component={Input}
                  className="input _opacity-gray"
                  error={
                    formik.touched.have_health_insurance &&
                    formik.errors.have_health_insurance
                  }
                />
              ) : (
                <span style={{ fontSize: "0.9rem" }}>
                  {formik.values.have_health_insurance || "Não informado"}
                </span>
              )}
            </div>

            <div
              className="item insurance-card-upload-trigger"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <span>
                <strong>Carteirinha (arquivo): </strong>
              </span>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <Button
                  type="button"
                  outlined
                  onClick={() => setIsInsuranceCardModalOpen(true)}
                >
                  Upload da carteirinha
                </Button>
                <span className="upload-helper-text text-light">
                  {uploadedInsuranceCardFileName ||
                    "Nenhum arquivo selecionado"}
                </span>
              </div>
            </div>
          </div>
          */}

          <div
            className="doctor"
            style={{ display: "flex", flexDirection: "column", gap: "5px" }}
          >
            <h3
              style={{
                color: "var(--color-purple)",
                fontSize: "1.2rem",
                marginBottom: "0",
              }}
            >
              Dados do Médico
            </h3>

            <div
              className="item"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <span>
                  <strong>Médico: </strong>
                </span>
                {editFields ? (
                  <Field
                    id="doctor"
                    type="text"
                    value={formik.values.doctor}
                    onChange={formik.handleChange}
                    component={Input}
                    className="input _opacity-gray"
                    error={formik.touched.doctor && formik.errors.doctor}
                  />
                ) : (
                  <span style={{ fontSize: "0.9rem" }}>
                    {formik.values.doctor || doctor || "Não informado"}
                  </span>
                )}
              </div>
            </div>

            <div
              className="item"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <span>
                  <strong>CRM: </strong>
                </span>
                {editFields ? (
                  <Field
                    id="crm"
                    type="text"
                    value={formik.values.crm}
                    onChange={formik.handleChange}
                    component={Input}
                    className="input _opacity-gray"
                    error={formik.touched.crm && formik.errors.crm}
                  />
                ) : (
                  <span style={{ fontSize: "0.9rem" }}>
                    {formik.values.crm || crm || "Não informado"}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="selected-unit">
            <h3
              style={{
                color: "var(--color-purple)",
                fontSize: "1.2rem",
                marginBottom: "0",
              }}
            >
              Edição de unidade
            </h3>

            <div className="item select-unit">
              {editFields ? (
                <Select
                  id="pre-order-conversion-unit"
                  placeholder="Selecione a unidade"
                  optionName="name"
                  options={units.data ?? []}
                  readOnly={blockUserInteraction}
                  value={formik.values.unit_id}
                  onChange={handleSelectedUnitChange}
                />
              ) : (
                <strong>
                  <span>{selectedUnitLabel}</span>
                </strong>
              )}
            </div>
          </div>
        </InfoDetailsStyled>
      </Form>
      {/* Modal de upload da carteirinha desativado nesta tela
      <InsuranceCardUploadModal
        isOpen={isInsuranceCardModalOpen}
        onClose={() => setIsInsuranceCardModalOpen(false)}
        onConfirm={handleInsuranceCardConfirm}
        blockInteraction={blockUserInteraction}
      />
      */}
    </>
  );
};
